export type SubtitleFormat = "srt" | "vtt" | "txt";

const stripVtt = (vtt: string) => {
  return vtt
    .replace(/\r/g, "")
    .split("\n")
    .filter((line) => line && line !== "WEBVTT" && !line.includes("-->") && !/^\d+$/.test(line.trim()))
    .join("\n");
};

const stripSrt = (srt: string) => {
  return srt
    .replace(/\r/g, "")
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return false;
      }

      if (/^\d+$/.test(trimmed)) {
        return false;
      }

      if (trimmed.includes("-->")) {
        return false;
      }

      return true;
    })
    .join("\n");
};

const buildSimpleVtt = (text: string) => {
  const trimmedText = text.trim();
  if (!trimmedText) {
    return "WEBVTT";
  }

  return ["WEBVTT", "", "00:00:00.000 --> 00:00:05.000", trimmedText].join("\n");
};

const srtToVtt = (srt: string) => {
  const normalized = srt.replace(/\r/g, "").trim();

  if (!normalized) {
    return "WEBVTT";
  }

  const blocks = normalized
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  const result = ["WEBVTT", ""];

  blocks.forEach((block) => {
    const lines = block.split("\n");
    const timeLineIndex = lines.findIndex((line) => line.includes("-->"));

    if (timeLineIndex === -1) {
      return;
    }

    const timeLine = lines[timeLineIndex].replace(/,/g, ".");
    const textLines = lines.slice(timeLineIndex + 1).filter(Boolean);

    if (textLines.length === 0) {
      return;
    }

    result.push(timeLine);
    result.push(...textLines);
    result.push("");
  });

  return result.join("\n").trim();
};

const vttToSrt = (vtt: string) => {
  const normalized = vtt.replace(/\r/g, "").trim();
  const blocks = normalized
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  const result: string[] = [];
  let index = 1;

  blocks.forEach((block) => {
    const lines = block.split("\n");
    const timeLineIndex = lines.findIndex((line) => line.includes("-->"));

    if (timeLineIndex === -1) {
      return;
    }

    const timeLine = lines[timeLineIndex].replace(/\./g, ",");
    const textLines = lines.slice(timeLineIndex + 1).filter(Boolean);

    if (textLines.length === 0) {
      return;
    }

    result.push(`${index++}`);
    result.push(timeLine);
    result.push(...textLines);
    result.push("");
  });

  return result.join("\n").trim();
};

export const convertSubtitleContentFormat = (
  content: string,
  sourceFormat: SubtitleFormat,
  targetFormat: SubtitleFormat
) => {
  if (sourceFormat === targetFormat) {
    return content;
  }

  switch (targetFormat) {
    case "txt":
      return sourceFormat === "vtt" ? stripVtt(content) : stripSrt(content);
    case "srt":
      if (sourceFormat === "txt") {
        return vttToSrt(buildSimpleVtt(content));
      }

      return vttToSrt(sourceFormat === "vtt" ? content : srtToVtt(content));
    default:
      if (sourceFormat === "txt") {
        return buildSimpleVtt(content);
      }

      return sourceFormat === "srt" ? srtToVtt(content) : content;
  }
};

export type SubtitleCue = {
  index: number;
  header?: string;
  timestamp: string;
  text: string;
};

const parseCuesFromBlocks = (content: string): SubtitleCue[] => {
  const normalized = content.replace(/\r/g, "").trim();
  if (!normalized) return [];

  const blocks = normalized
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  const cues: SubtitleCue[] = [];

  blocks.forEach((block) => {
    if (block === "WEBVTT" || block.startsWith("WEBVTT")) return;
    if (block.startsWith("NOTE") || block.startsWith("STYLE") || block.startsWith("REGION")) return;

    const lines = block.split("\n");
    const timeLineIndex = lines.findIndex((line) => line.includes("-->"));
    if (timeLineIndex === -1) return;

    const header = timeLineIndex > 0 ? lines.slice(0, timeLineIndex).join("\n").trim() : undefined;
    const timestamp = lines[timeLineIndex].trim();
    const textLines = lines.slice(timeLineIndex + 1).filter((line) => line.length > 0);
    if (textLines.length === 0) return;

    cues.push({
      index: cues.length,
      header: header && !/^\d+$/.test(header) ? header : undefined,
      timestamp,
      text: textLines.join("\n"),
    });
  });

  return cues;
};

export const parseVttCues = (vtt: string): SubtitleCue[] => parseCuesFromBlocks(vtt);

export const parseSrtCues = (srt: string): SubtitleCue[] => {
  const vttLike = srtToVtt(srt);
  return parseCuesFromBlocks(vttLike);
};

export const buildVttFromCues = (cues: SubtitleCue[]): string => {
  const parts: string[] = ["WEBVTT", ""];
  cues.forEach((cue) => {
    if (cue.header) parts.push(cue.header);
    parts.push(cue.timestamp.replace(/,/g, "."));
    parts.push(cue.text);
    parts.push("");
  });
  return parts.join("\n").trim() + "\n";
};

export const detectSubtitleFormat = (content: string): "vtt" | "srt" => {
  return content.trim().startsWith("WEBVTT") ? "vtt" : "srt";
};

/** Start-of-cue seconds from a timing line like "00:01:02.500 --> 00:01:05.000". */
const parseCueStartSeconds = (timestamp: string): number | null => {
  const start = timestamp.split("-->")[0]?.trim() ?? "";
  const match = start.match(/^(?:(\d{1,3}):)?(\d{1,2}):(\d{2})[.,]\d{1,3}/);
  if (!match) return null;
  const [, h, m, s] = match;
  return (h ? parseInt(h, 10) * 3600 : 0) + parseInt(m, 10) * 60 + parseInt(s, 10);
};

export const formatCueTime = (seconds: number, forceHours = false): string => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 || forceHours ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const cleanCueText = (text: string): string =>
  text
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * One line per cue: "[mm:ss] text" ("[hh:mm:ss]" once the video passes 1h),
 * the format the AI endpoints detect to enable timestamped prompts.
 */
export const cuesToTimestampedText = (
  cues: Array<{ startTime: number; text: string }>
): string => {
  if (cues.length === 0) return "";
  // Uniform width across the whole file so "[12:34]" can't be misread as
  // 12m34s in a video that later crosses the 1h mark.
  const forceHours = cues[cues.length - 1].startTime >= 3600;
  return cues
    .map((cue) => {
      const text = cleanCueText(cue.text);
      return text ? `[${formatCueTime(cue.startTime, forceHours)}] ${text}` : "";
    })
    .filter(Boolean)
    .join("\n");
};

/** Raw SRT/VTT content → timestamped lines. Returns "" when no cue parses. */
export const subtitleToTimestampedText = (content: string): string => {
  const cues =
    detectSubtitleFormat(content) === "vtt" ? parseVttCues(content) : parseSrtCues(content);
  const timedCues: Array<{ startTime: number; text: string }> = [];
  cues.forEach((cue) => {
    const startTime = parseCueStartSeconds(cue.timestamp);
    if (startTime !== null) timedCues.push({ startTime, text: cue.text });
  });
  return cuesToTimestampedText(timedCues);
};

/**
 * Keep long transcripts under the AI endpoints' 200k-char cap by sampling
 * lines evenly across the whole span (the server only head-slices, which
 * would drop the end of the video). First and last lines are always kept.
 */
export const sampleSubtitleLines = (text: string, maxChars = 180_000): string => {
  if (text.length <= maxChars) return text;

  const lines = text.split("\n").filter((line) => line.trim());
  if (lines.length <= 2) return text.slice(0, maxChars);

  let keep = Math.max(2, Math.floor((lines.length * maxChars) / text.length));
  while (keep >= 2) {
    const step = (lines.length - 1) / (keep - 1);
    const indices = new Set<number>();
    for (let i = 0; i < keep; i++) {
      indices.add(Math.round(i * step));
    }
    const sampled = [...indices].map((index) => lines[index]).join("\n");
    if (sampled.length <= maxChars) return sampled;
    keep = Math.floor(keep * 0.9);
  }

  return text.slice(0, maxChars);
};

export const getSubtitleMimeType = (format: SubtitleFormat) => {
  switch (format) {
    case "txt":
      return "text/plain;charset=utf-8";
    case "vtt":
      return "text/vtt;charset=utf-8";
    default:
      return "application/x-subrip;charset=utf-8";
  }
};
